/**
 * (c) 2024, Micro:bit Educational Foundation and contributors
 *
 * SPDX-License-Identifier: MIT
 */
import { ReactNode } from "react";
import {
  Box,
  BoxProps,
  darkSurface,
  HStack,
  HstackProps,
  VStack,
} from "@microbit/ui";

export interface ActionBarProps extends BoxProps {
  itemsLeft?: ReactNode;
  itemsCenter?: ReactNode;
  itemsRight?: ReactNode;
  itemsLeftProps?: HstackProps;
  itemsCenterProps?: HstackProps;
}

/**
 * Overlap between status bar area and ActionBar content area.
 * Reduces visual "double padding" when both have centered content.
 * Only applied when there's a non-zero safe area inset.
 */
const statusBarOverlap = "12px";

const ActionBar = ({
  itemsLeft,
  itemsCenter,
  itemsRight,
  itemsLeftProps,
  itemsCenterProps,
  ...rest
}: ActionBarProps) => {
  return (
    <VStack
      as="header"
      gap={0}
      {...darkSurface}
      bg="statusBarBg"
      css={{ "--inset-top": "env(safe-area-inset-top)" }}
      {...rest}
    >
      {/* Status bar spacer: full inset minus overlap (but never negative) */}
      <Box
        flexShrink={0}
        h={`max(0px, calc(var(--inset-top) - ${statusBarOverlap}))`}
      />
      {/* ActionBar content */}
      <HStack
        alignItems="center"
        justifyContent="space-between"
        gap={0}
        h="64px"
        w="100%"
        pl="var(--window-controls-left, 0px)"
      >
        <HStack
          flex={itemsCenter ? { base: "0 1 max-content", xl: "1 0" } : "4 0"}
          justifyContent="flex-start"
          {...itemsLeftProps}
        >
          {itemsLeft}
        </HStack>
        {itemsCenter ? (
          <HStack
            flex="2 1"
            justifyContent="center"
            px={3}
            {...itemsCenterProps}
          >
            {itemsCenter}
          </HStack>
        ) : null}
        <HStack
          flex={{ base: "0 1 max-content", xl: "1 0" }}
          justifyContent="flex-end"
        >
          {itemsRight}
        </HStack>
      </HStack>
    </VStack>
  );
};

export default ActionBar;
